import Card from "components/card/Card";
import Page from "components/page/Page";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import {
    IExchangeCalculateResponse,
    IExchangeGroup,
    IExchangePaymentTransaction,
    IExchangeRefundTransaction,
    PostCalculateExchange,
    PostExchange,
} from "api/rpc/2024-04/facilityAdmin/order/exchange";
import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { loadPaymentOptions } from "redux/actions/facility";
import { errorMessage, valueToString } from "helpers/Helpers";
import axios, { CancelToken } from "axios";
import { IOrder, IOrderLineItem } from "redux/reducers/models/order";
import { IProduct } from "redux/reducers/models/product";
import ReactDOM from "react-dom";
import "./exchangeItems.scss";
import { LocaleCurrency } from "helpers/Locale";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUTurnUpLeft, faCircleArrowDown, faLongArrowRight, faPen, faSpinnerThird, faXmark } from "@fortawesome/pro-regular-svg-icons";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import ExchangeItemsModal from "./ExchangeItemsModal";
import ExchangeCheckoutSheet from "./ExchangeCheckoutSheet";
import Checkbox from "components/form/checkbox/Checkbox";
import Avatar from "components/avatar/Avatar";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";

interface IExchangeItemGroup {
    exchange_item_id: string;
    line_item_id?: number;
    previous_items: Array<IOrderLineItem>;
    new_items: Array<IProduct>;
}

export default function ExchangeItems() {
    const { t } = useTranslation();
    const { orderId } = useParams<{ orderId: string }>();
    const dispatch = useAppDispatch();
    const { displayCurrency } = useCurrencyFacility();
    const { StripeTerminalRefundSheet, StripeTerminalSheet, processRefund, processPayment, paymentStatus, capturedTransactionId } = useStripeTerminalPayment();
    const history = useHistory();
    const { facilityStore, terminalStore } = useAppSelector(store => store);
    const [order, setOrder] = useState<IOrder>();
    const [exchangeItems, setExchangeItems] = useState<Array<IExchangeItemGroup>>();
    const { state: cancelModal, updateModal: updateCancelModal, closeModal: closeCloseModal } = useModal();
    const { state: exchangeItemsModal, updateModal: updateExchangeItemsModal, closeModal: closeExchangeItemsModal } = useModal<{ exchangeItemId: string, itemReplacementTitle: string, lineItemId: number, presetItems: Array<IProduct>, lineItemIds: Array<number> }>({ exchangeItemId: null, itemReplacementTitle: "", lineItemId: null, presetItems: null, lineItemIds: null });
    const { state: editPriceModal, updateModal: updateEditPriceModal, closeModal: closeEditPriceModal } = useModal<{ price: string; product: IProduct; groupIndex: number; itemIndex: number }>({ price: "", product: null, groupIndex: null, itemIndex: null });

    const [combineState, setCombineState] = useState<Array<IExchangeItemGroup>>([]);
    const [combine, setCombine] = useState<boolean>(false);
    const [summary, setSummary] = useState<IExchangeCalculateResponse | null>(null);
    const [calculating, setCalculating] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pendingCardCollect, setPendingCardCollect] = useState(false);
    const pendingCardCollectRef = useRef<{
        order_id: number;
        exchanges: IExchangeGroup[];
        net_balance: number;
        paymentTransaction: IExchangePaymentTransaction;
    } | null>(null);

    const itemsSelected = useMemo(() => {
        return exchangeItems?.some(group => group?.new_items?.length > 0);
    }, [exchangeItems]);

    const exchangeParams = useMemo(() => buildExchangeParams(exchangeItems), [exchangeItems]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getOrder(source.token);
        void dispatch(loadPaymentOptions(source.token));
        return () => source.cancel();
    }, []);


    async function getOrder(cancelToken: CancelToken) {
        const orderRes = await GetOrder({ id: Number(orderId), extended: true }, true, cancelToken);

        if (orderRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(errorMessage(orderRes, t("Error getting order details"))));
            return;
        }

        const order: IOrder = orderRes?.data[0];
        const exchangeItems = order?.line_items?.reduce((items, lineItem) => {
            const updatedItems = [...items];

            const returnedQuantity = order?.exchanges?.reduce((quantity, exchange) => {
                return quantity + (exchange?.exchange_line_items
                    ?.filter(exchangeItem =>
                        exchangeItem?.type === "return" &&
                        exchangeItem?.order_line_item_id === lineItem?.id,
                    )
                    ?.reduce((sum, exchangeItem) => sum + (exchangeItem?.quantity ?? 1), 0) ?? 0);
            }, 0);

            const remainingQuantity = lineItem.quantity - returnedQuantity;

            for (let i = 0; i < remainingQuantity; i++) {
                const exchangeItem = {
                    previous_items: [lineItem],
                    new_items: [] as Array<IProduct>,
                    line_item_id: lineItem.id,
                    exchange_item_id: `${lineItem.id}-${returnedQuantity + i}`,
                };

                updatedItems.push(exchangeItem);
            }

            return updatedItems;
        }, [] as Array<IExchangeItemGroup>);

        ReactDOM.unstable_batchedUpdates(() => {
            setOrder(orderRes?.data[0] as IOrder);
            setExchangeItems(exchangeItems);
        });
    }

    function addReplacementItems(items: Array<IProduct>) {
        if (combine && combineState?.length > 0) {
            const prevItems: Array<IOrderLineItem> = [];

            combineState.forEach(group => {
                prevItems.push(...group.previous_items);
            });

            const combination: IExchangeItemGroup = {
                exchange_item_id: `combined-${Date.now()}`,
                previous_items: prevItems,
                new_items: items,
            };

            const selectedExchangeItemIds = new Set(
                combineState.map(group => group.exchange_item_id),
            );

            const updatedExchangeItems = exchangeItems?.filter(
                group => !selectedExchangeItemIds.has(group.exchange_item_id),
            );

            ReactDOM.unstable_batchedUpdates(() => {
                setCombine(false);
                setCombineState([]);
                setExchangeItems([
                    combination,
                    ...updatedExchangeItems,
                ]);
            });

        } else {
            const foundExchangeGroupIndex = exchangeItems?.findIndex(group => group?.line_item_id === exchangeItemsModal?.lineItemId);
            if (foundExchangeGroupIndex !== -1) {
                const updatedExchangeItems = [...exchangeItems];
                updatedExchangeItems[foundExchangeGroupIndex].new_items = items;
                setExchangeItems(updatedExchangeItems);
            }
        }
    }

    function clearExchange(index: number) {
        const group = exchangeItems?.[index];

        if (!group) {
            return;
        }

        const separateGroups = group.previous_items.map((item, itemIndex) => ({
            exchange_item_id: `${item.id}-${Date.now()}-${itemIndex}`,
            line_item_id: item.id,
            previous_items: [item],
            new_items: [],
        }));

        const updatedExchangeItems = [...exchangeItems];

        updatedExchangeItems.splice(index, 1, ...separateGroups);

        setExchangeItems(updatedExchangeItems);
    }

    function onCombineClick(
        e: React.ChangeEvent<HTMLInputElement>,
        exchangeItemGroup: IExchangeItemGroup,
    ) {
        const { checked } = e.target;

        if (checked) {
            setCombineState(prev => [...prev, exchangeItemGroup]);
        } else {
            setCombineState(prev =>
                prev.filter(
                    item =>
                        item.exchange_item_id !== exchangeItemGroup.exchange_item_id,
                ),
            );
        }
    }

    function onCombineClickDiv(
        e: React.MouseEvent<HTMLDivElement>,
        exchangeItemGroup: IExchangeItemGroup,
    ) {
        if (combineState?.some(exchangeGroup => exchangeGroup?.exchange_item_id === exchangeItemGroup?.exchange_item_id)) {
            setCombineState(prev =>
                prev.filter(
                    item =>
                        item.exchange_item_id !== exchangeItemGroup.exchange_item_id,
                ),
            );
        } else {
            setCombineState(prev => [...prev, exchangeItemGroup]);
        }
    }

    function handleCombineSelectAll(e: React.ChangeEvent<HTMLInputElement>) {
        const { checked } = e.target;

        if (checked) {
            setCombineState(
                exchangeItems?.filter(
                    group =>
                        group.previous_items.length === 1 &&
                        group.new_items.length === 0,
                ) ?? [],
            );
        } else {
            setCombineState([]);
        }
    }

    function handleUpdatePrice() {
        const updatedExchangeItems = exchangeItems ? [...exchangeItems] : [];
        const updatedProduct = exchangeItems[editPriceModal?.groupIndex]?.new_items[editPriceModal?.itemIndex];
        const updatedVariant = updatedProduct?.variants[0];
        exchangeItems[editPriceModal.groupIndex].new_items[editPriceModal.itemIndex] = { ...updatedProduct, variants: [{ ...updatedVariant, price: Number(editPriceModal?.price) }] };
        setExchangeItems(updatedExchangeItems);
        void closeEditPriceModal();
    }

    useEffect(() => {
        if (!order?.id || exchangeParams.length === 0) {
            setSummary(null);
            return;
        }

        let cancelled = false;
        const timeout = global.setTimeout(() => {
            void calculateExchange(false).then(() => undefined);
        }, 400);

        async function calculateExchange(useGlobalLoader: boolean) {
            ReactDOM.unstable_batchedUpdates(() => {
                setCalculating(true);
                setSummary(null);
            });

            const calculateRes = await PostCalculateExchange(
                { order_id: order.id, exchanges: exchangeParams },
                useGlobalLoader,
            );

            if (cancelled) {
                return calculateRes;
            }

            setCalculating(false);

            if (calculateRes.status !== StatusCode.OK) {
                setSummary(null);
                dispatch(showError(errorMessage(calculateRes, t("Unable to calculate exchange"))));
                return calculateRes;
            }

            setSummary(calculateRes.data);
            return calculateRes;
        }

        return () => {
            cancelled = true;
            clearTimeout(timeout);
            setCalculating(false);
        };
    }, [order?.id, JSON.stringify(exchangeParams)]);

    useEffect(() => {
        if (pendingCardCollect && capturedTransactionId && pendingCardCollectRef.current) {
            pendingCardCollectRef.current.paymentTransaction = {
                ...pendingCardCollectRef.current.paymentTransaction,
                transaction_id: capturedTransactionId,
            };
        }
    }, [capturedTransactionId, pendingCardCollect]);

    useEffect(() => {
        if (!pendingCardCollect) {
            return;
        }

        if (paymentStatus === "completed") {
            const pending = pendingCardCollectRef.current;
            pendingCardCollectRef.current = null;
            setPendingCardCollect(false);

            if (!pending) {
                setSubmitting(false);
                dispatch(showError(t("Unable to complete exchange")));
                setCheckoutOpen(true);
                return;
            }

            void (async () => {
                const paymentTransaction: IExchangePaymentTransaction = {
                    ...pending.paymentTransaction,
                    transaction_id: pending.paymentTransaction.transaction_id ?? capturedTransactionId,
                };

                if (!paymentTransaction.transaction_id) {
                    setSubmitting(false);
                    dispatch(showError(t("Unable to complete exchange")));
                    setCheckoutOpen(true);
                    return;
                }

                const exchangeRes = await PostExchange(
                    {
                        order_id: pending.order_id,
                        exchanges: pending.exchanges,
                        net_balance: pending.net_balance,
                        payment_transactions: [paymentTransaction],
                    },
                    true,
                );

                setSubmitting(false);

                if (exchangeRes.status !== StatusCode.OK) {
                    dispatch(showError(errorMessage(exchangeRes, t("Unable to complete exchange"))));
                    setCheckoutOpen(true);
                    return;
                }

                setCheckoutOpen(false);
                dispatch(showSuccess(t("Exchange completed")));
                history.push(`/admin/order/${orderId}`);
            })();
            return;
        }

        if (paymentStatus === "canceled") {
            pendingCardCollectRef.current = null;
            ReactDOM.unstable_batchedUpdates(() => {
                setPendingCardCollect(false);
                setSubmitting(false);
                setCheckoutOpen(true);
            });
        }
    }, [paymentStatus, pendingCardCollect, capturedTransactionId]);

    async function handleCheckout() {
        if (!order?.id || exchangeParams.length === 0) {
            return;
        }

        const calculateRes = await PostCalculateExchange(
            { order_id: order.id, exchanges: exchangeParams },
            true,
        );

        if (calculateRes.status !== StatusCode.OK) {
            setSummary(null);
            dispatch(showError(errorMessage(calculateRes, t("Unable to calculate exchange"))));
            return;
        }

        setSummary(calculateRes.data);

        if (calculateRes.data.settlement_type === "even") {
            await commitExchange({
                order_id: order.id,
                exchanges: exchangeParams,
                net_balance: calculateRes.data.net_balance,
            });
            return;
        }

        setCheckoutOpen(true);
    }

    async function commitExchange(params: Parameters<typeof PostExchange>[0]) {
        if (submitting) {
            return;
        }

        setSubmitting(true);
        const exchangeRes = await PostExchange(params, true);
        setSubmitting(false);

        if (exchangeRes.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(exchangeRes, t("Unable to complete exchange"))));
            return;
        }

        setCheckoutOpen(false);
        dispatch(showSuccess(t("Exchange completed")));
        history.push(`/admin/order/${orderId}`);
    }

    async function handleConfirmRefund(refundTransactions: IExchangeRefundTransaction[]) {
        if (!order?.id || !summary) {
            return;
        }

        let tenders = refundTransactions.filter(txn => Number(txn.amount) > 0);
        const interacTenders = tenders.filter(txn => {
            const parent = order.transactions?.find(transaction => transaction.id === txn.parent_id);
            const paymentType = parent?.payment_type?.toLowerCase();
            const paymentMethod = parent?.payment_method?.toLowerCase();
            return (paymentType === "interac" || paymentMethod === "interac") && Number(txn.amount) > 0;
        });

        if (interacTenders.length > 0) {
            if (!terminalStore?.reader) {
                dispatch(showError(t("A card reader is required to refund Interac")));
                return;
            }

            setCheckoutOpen(false);
            setSubmitting(true);

            for (const tender of interacTenders) {
                const parent = order.transactions?.find(transaction => transaction.id === tender.parent_id);
                if (!parent?.payment_intent_id) {
                    tenders = tenders.filter(txn => txn.parent_id !== tender.parent_id);
                    continue;
                }

                const refundRes = await processRefund(Number(tender.amount), String(parent.payment_intent_id));
                if (refundRes !== "success") {
                    tenders = tenders.filter(txn => txn.parent_id !== tender.parent_id);
                }
            }

            setSubmitting(false);

            if (tenders.length === 0) {
                setCheckoutOpen(true);
                dispatch(showError(t("Unable to complete Interac refund")));
                return;
            }

            const remainingRefund = tenders.reduce((sum, txn) => sum + Number(txn.amount), 0);
            if (Math.abs(remainingRefund - Math.abs(Number(summary.net_balance))) > 0.009) {
                setCheckoutOpen(true);
                dispatch(showError(t("Refund tenders must equal the net exchange refund")));
                return;
            }
        }

        await commitExchange({
            order_id: order.id,
            exchanges: exchangeParams,
            net_balance: summary.net_balance,
            refund_transactions: tenders,
        });
    }

    async function handleConfirmCollect(paymentTransaction: IExchangePaymentTransaction) {
        if (!order?.id || !summary) {
            return;
        }

        const isCardCollect =
            paymentTransaction.payment_method === "card" ||
            facilityStore?.paymentOptions?.find(option => option.id === paymentTransaction.payment_option_id)
                ?.processing_type === "terminal";

        if (isCardCollect) {
            if (!terminalStore?.reader) {
                dispatch(showError(t("A card reader is required to collect card payment")));
                return;
            }

            const paymentOption =
                facilityStore?.paymentOptions?.find(option => option.id === paymentTransaction.payment_option_id) ??
                ({
                    id: paymentTransaction.payment_option_id,
                    payment_method: paymentTransaction.payment_method,
                } as Record<string, any>);

            ReactDOM.unstable_batchedUpdates(() => {
                setSubmitting(true);
                setPendingCardCollect(true);
                setCheckoutOpen(false);
            });

            pendingCardCollectRef.current = {
                order_id: order.id,
                exchanges: exchangeParams,
                net_balance: summary.net_balance,
                paymentTransaction,
            };

            await processPayment(
                order.id,
                paymentOption as Record<string, any>,
                Number(paymentTransaction.amount),
                false,
                false,
                undefined,
                undefined,
                undefined,
                true,
                true,
            );
            return;
        }

        await commitExchange({
            order_id: order.id,
            exchanges: exchangeParams,
            net_balance: summary.net_balance,
            payment_transactions: [paymentTransaction],
        });
    }

    return (
        <Page
            title={t("Exchange items for Order #{{orderNumber}}", { orderNumber: order?.number })}
            splitLayout
            breadcrumbs={[{ prefix: true, label: t("Back to Order"), url: `/admin/order/${orderId}` }]}
            subtitle={t("Taxes and discounts are included for all items.")}
        >
            <Page.Section twoThirds>
                <Card title={t("Items")} titleActions={exchangeItems?.length > 1 ? [{
                    content: t("Combine items"), action: () => ReactDOM.unstable_batchedUpdates(() => {
                        setCombine(prev => !prev);
                        setCombineState([]);
                    }),
                }] : undefined}>
                    <Card.Section>
                        {combine ? (
                            <div className="flex justify-between items-center gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                    <p style={{ fontSize: "14px", fontWeight: "500", color: "#344054" }}>{combineState?.length} {t("selected")}</p>
                                    <div className="ui-exchange-items-divider-vertical"></div>
                                    <Checkbox
                                        label={t("Select all")}
                                        size="small"
                                        onChange={handleCombineSelectAll}
                                        checked={
                                            exchangeItems?.length > 0 &&
                                            combineState?.length === exchangeItems.length
                                        }
                                        indeterminate={
                                            combineState?.length > 0 &&
                                            combineState?.length !== exchangeItems?.length
                                        } />
                                </div>
                                <Button type="text" size="small" onClick={() => setCombineState([])}><FontAwesomeIcon icon={faXmark} className="mr-2" /> {t("Clear all")}</Button>
                            </div>
                        ) : null}
                        <div className="ui-exchange-items-list">
                            {exchangeItems?.map((exchangeItemGroup, index) => {
                                if (exchangeItemGroup?.new_items?.length === 0) {
                                    return (
                                        <div key={index} className="flex items-center gap-4">
                                            {combine ? (
                                                <Checkbox
                                                    labelHidden
                                                    size="medium"
                                                    checked={combineState?.some(
                                                        combine =>
                                                            combine.exchange_item_id === exchangeItemGroup.exchange_item_id,
                                                    )}
                                                    onChange={e => onCombineClick(e, exchangeItemGroup)}
                                                />
                                            ) : null}
                                            <div className="ui-exchange-items-default-card">
                                                {exchangeItemGroup?.previous_items?.map((exchangeItem, itemIndex) => {
                                                    const productTitle = exchangeItem?.preferred_title ? exchangeItem?.preferred_title : exchangeItem?.product_title;
                                                    return (
                                                        <div key={itemIndex} className="ui-exchange-items-group" onClick={combine ? 
                                                            e => onCombineClickDiv(e, exchangeItemGroup)
                                                            : () => updateExchangeItemsModal({
                                                            isOpen: true,
                                                            itemReplacementTitle: `${productTitle} ${(productTitle !== exchangeItem?.variant_title &&
                                                                exchangeItem?.product_title !== exchangeItem?.variant_title)
                                                                ? `- ${exchangeItem?.variant_title}`
                                                                : ""
                                                                }`,
                                                            exchangeItemId: exchangeItemGroup.exchange_item_id,
                                                            lineItemId: exchangeItemGroup.line_item_id,
                                                        })}>
                                                            <div className="ui-exchange-items-details">
                                                                <p className="ui-exchange-items-quantity">1 x</p>
                                                                <div>
                                                                    <p className="ui-exchange-items-product-title">{productTitle} {(productTitle !== exchangeItem?.variant_title && exchangeItem?.product_title !== exchangeItem?.variant_title) ? `- ${exchangeItem?.variant_title}` : ""}</p>
                                                                    <p><LocaleCurrency amount={exchangeItem?.price} currency={facilityStore?.facility?.default_currency_code} style={{ fontSize: "14px", fontWeight: "500", color: "#344054" }} /></p>
                                                                </div>
                                                            </div>
                                                            {!combine ? (
                                                                <Button type="secondary" size="medium" onClick={e => {
                                                                    e.stopPropagation();
                                                                    void updateExchangeItemsModal({
                                                                        isOpen: true,
                                                                        itemReplacementTitle: `${productTitle} ${(productTitle !== exchangeItem?.variant_title &&
                                                                            exchangeItem?.product_title !== exchangeItem?.variant_title)
                                                                            ? `- ${exchangeItem?.variant_title}`
                                                                            : ""
                                                                            }`,
                                                                        exchangeItemId: exchangeItemGroup.exchange_item_id,
                                                                        lineItemId: exchangeItemGroup.line_item_id,
                                                                    });
                                                                }}>{t("Exchange")}</Button>
                                                            ) : null}

                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div key={index} className="ui-exchange-items-replacement-card">
                                            <div className="ui-exchange-items-replacement-card-inner">
                                                {exchangeItemGroup?.previous_items?.map((exchangeItem, itemIndex) => {
                                                    const productTitle = exchangeItem?.preferred_title ? exchangeItem?.preferred_title : exchangeItem?.product_title;
                                                    return (
                                                        <div key={itemIndex} className="ui-exchange-items-group">
                                                            <div className="ui-exchange-items-details">
                                                                <p className="ui-exchange-items-quantity">1 x</p>
                                                                <div>
                                                                    <p className="ui-exchange-items-product-title" style={{ textDecoration: "line-through" }}>{productTitle} {(productTitle !== exchangeItem?.variant_title && exchangeItem?.product_title !== exchangeItem?.variant_title) ? `- ${exchangeItem?.variant_title}` : ""}</p>

                                                                </div>
                                                            </div>
                                                            <p><LocaleCurrency amount={exchangeItem?.price} currency={facilityStore?.facility?.default_currency_code} style={{ fontSize: "14px", fontWeight: "500", color: "#344054", textDecoration: "line-through" }} /></p>
                                                        </div>
                                                    );
                                                })}
                                                <div className="ui-exchange-items-divider-group">
                                                    <div className="ui-exchange-items-divider"></div>
                                                    <FontAwesomeIcon icon={faCircleArrowDown} style={{ color: "#039855", fontSize: "20px" }} />
                                                    <div className="ui-exchange-items-divider"></div>
                                                </div>
                                                {exchangeItemGroup?.new_items?.map((exchangeItem, itemIndex) => {
                                                    const productTitle = exchangeItem?.preferred_title ? exchangeItem?.preferred_title : exchangeItem?.title;
                                                    const variant = exchangeItem?.variants[0];
                                                    return (
                                                        <div key={itemIndex} className="ui-exchange-items-group">
                                                            <div className="ui-exchange-items-details">
                                                                <p className="ui-exchange-items-quantity">{variant?.quantity ?? 1} x</p>
                                                                <div>
                                                                    <p className="ui-exchange-items-product-title">{productTitle} {(productTitle !== variant?.title && exchangeItem?.title !== variant?.title) ? `- ${variant?.title}` : ""}</p>

                                                                </div>
                                                            </div>
                                                            <p><LocaleCurrency amount={variant?.price * variant?.quantity} currency={facilityStore?.facility?.default_currency_code} style={{ fontSize: "14px", fontWeight: "500", color: "#344054" }} />
                                                                <FontAwesomeIcon icon={faPen} style={{ fontSize: "14px", fontWeight: "500", color: "#344054", cursor: "pointer", marginLeft: "8px" }} onClick={() => updateEditPriceModal({ isOpen: true, price: String(exchangeItem?.variants[0]?.price), product: exchangeItem, groupIndex: index, itemIndex: itemIndex })} /></p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <Button size="medium" type="secondary" block onClick={() => clearExchange(index)}><FontAwesomeIcon icon={faArrowUTurnUpLeft} className="mr-2" /> {t("Undo return")}</Button>
                                                <Button size="medium" type="primary" block onClick={() => updateExchangeItemsModal({
                                                    isOpen: true,
                                                    itemReplacementTitle: `${exchangeItemGroup?.previous_items[0]?.preferred_title ? exchangeItemGroup?.previous_items[0]?.preferred_title : exchangeItemGroup?.previous_items[0]?.product_title}`,
                                                    lineItemId: exchangeItemGroup?.line_item_id,
                                                    presetItems: exchangeItemGroup?.new_items,
                                                })}><FontAwesomeIcon icon={faPen} className="mr-2" /> {t("Edit replacement")}</Button>
                                            </div>
                                        </div>
                                    );
                                }

                            })}
                        </div>
                        {combine ? (
                            <div className="flex justify-between gap-4 mt-4">
                                <Button size="medium" type="secondary" block onClick={() => {
                                    ReactDOM.unstable_batchedUpdates(() => {
                                        setCombine(false);
                                        setCombineState([]);
                                    });
                                }}>{t("Cancel")}</Button>
                                <Button size="medium" type="primary" disabled={combineState?.length <= 1} block onClick={() => {
                                    updateExchangeItemsModal({
                                        isOpen: true,
                                        itemReplacementTitle: combineState
                                            ?.map(group => {
                                                const item = group.previous_items[0];

                                                return `${item?.preferred_title
                                                    ? item.preferred_title
                                                    : item.product_title
                                                    } ${item?.product_title !== item?.variant_title
                                                        ? `- ${item?.variant_title}`
                                                        : ""
                                                    }`;
                                            })
                                            ?.join(", "),
                                        lineItemIds: combineState?.map(group => group.line_item_id),
                                    });
                                }}>{t("Combine {{items}} items", { items: combineState?.length })}</Button>
                            </div>
                        ) : null}
                    </Card.Section>
                </Card>
            </Page.Section>
            <Page.Section oneThird>
                <Card title={t("Exchange summary")}>
                    <Card.Section>
                        {summary ? (
                            <>
                                <Card.SubSection>
                                    <div className="flex items-center justify-between">
                                        <p className="text-lg">{t("Returned items")}</p>
                                        <LocaleCurrency currency={summary.currency} amount={-Number(summary.return_subtotal)} />
                                    </div>
                                    {Number(summary.return_tax) !== 0 ? (
                                        <div className="flex items-center justify-between">
                                            <p className="text-lg">{t("Returned tax")}</p>
                                            <LocaleCurrency currency={summary.currency} amount={-Number(summary.return_tax)} />
                                        </div>
                                    ) : null}
                                </Card.SubSection>
                                <Card.SubSection>
                                    <div className="flex items-center justify-between">
                                        <p className="text-lg">{t("Replacement items")}</p>
                                        <LocaleCurrency currency={summary.currency} amount={summary.exchange_subtotal} />
                                    </div>
                                    {Number(summary.exchange_tax) !== 0 ? (
                                        <div className="flex items-center justify-between">
                                            <p className="text-lg">{t("Replacement tax")}</p>
                                            <LocaleCurrency currency={summary.currency} amount={summary.exchange_tax} />
                                        </div>
                                    ) : null}
                                </Card.SubSection>
                                {summary.tax_lines?.length > 0 ? (
                                    <Card.SubSection>
                                        {summary.tax_lines.map((taxLine, i) => (
                                            <div key={i} className="flex items-center justify-between">
                                                <p className="text-lg">{taxLine.title}</p>
                                                <LocaleCurrency currency={summary.currency} amount={taxLine.price} />
                                            </div>
                                        ))}
                                    </Card.SubSection>
                                ) : null}
                                <Card.SubSection>
                                    <div className="flex items-center justify-between">
                                        <p className="text-lg font-semibold">
                                            {summary.settlement_type === "refund"
                                                ? t("Refund due")
                                                : summary.settlement_type === "collect"
                                                    ? t("Amount due")
                                                    : t("Even exchange")}
                                        </p>
                                        <LocaleCurrency
                                            currency={summary.currency}
                                            amount={
                                                summary.settlement_type === "refund"
                                                    ? Math.abs(Number(summary.net_balance))
                                                    : Number(summary.amount_due)
                                            }
                                        />
                                    </div>
                                </Card.SubSection>
                                {summary.settlement_type === "refund" && summary.refund_transactions?.some(txn => Number(txn.amount) > 0) ? (
                                    <Card.SubSection>
                                        <p className="font-semibold mb-3">{t("Suggested refund tenders")}</p>
                                        {summary.refund_transactions
                                            .filter(txn => Number(txn.amount) > 0)
                                            .map((txn, i) => {
                                                const parent = order?.transactions?.find(transaction => transaction.id === txn.parent_id);
                                                const tenderLabel = parent?.payment_type ?? parent?.payment_method ?? t("Payment");
                                                return (
                                                    <div key={txn.parent_id ?? i} className="flex items-center justify-between">
                                                        <p className="text-lg">{valueToString(tenderLabel)}</p>
                                                        <LocaleCurrency currency={summary.currency} amount={txn.amount} />
                                                    </div>
                                                );
                                            })}
                                    </Card.SubSection>
                                ) : null}
                            </>
                        ) : (
                            <p className="text-lg">{itemsSelected && calculating ? <div><FontAwesomeIcon icon={faSpinnerThird} spin /> {t("Calculating…")}</div> : t("Select replacement items to see totals.")}</p>
                        )}
                    </Card.Section>
                    <Card.Section>
                        <div className="flex flex-col gap-4">
                            <Button
                                disabled={!itemsSelected || calculating || submitting || !summary}
                                block
                                type="primary"
                                size="medium"
                                onClick={() => void handleCheckout()}
                            >
                                {summary?.settlement_type === "refund"
                                    ? t("Issue refund")
                                    : summary?.settlement_type === "collect"
                                        ? t("Collect payment")
                                        : summary?.settlement_type === "even"
                                            ? t("Complete exchange")
                                            : t("Checkout")}
                                {" "}
                                <FontAwesomeIcon icon={faLongArrowRight} />
                            </Button>
                            <div>
                                <Button onClick={itemsSelected ? () => updateCancelModal({ isOpen: true }) : () => history?.push(`/admin/order/${orderId}`)} block type="secondary" size="medium">{t("Cancel")}</Button>
                            </div>
                        </div>
                    </Card.Section>
                </Card>
                <Card title={t("Customer")}>
                    <Card.Section>
                        <div>
                            {order?.customer ? (
                                <div className="flex gap-4">
                                    <Avatar size="xxlarge" source={order?.customer?.profile_image_source} />
                                    <div>
                                        <p className="text-lg text-semibold">{order?.customer?.full_name}</p>
                                        <p className="text-lg">{order?.customer?.email}</p>
                                        <p className="text-lg">{order?.customer?.phone}</p>
                                        <p className="text-lg">{order?.customer?.customer_type}</p>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <p>{t("No customer")}</p>
                                </div>
                            )}
                        </div>
                    </Card.Section>
                </Card>
            </Page.Section>

            <Portal isMounted={editPriceModal?.isOpen}>
                <Sheet
                    open={editPriceModal?.isOpen}
                    title={t("Edit replacement price")}
                    supportText={`${t("For")} ${editPriceModal?.product?.preferred_title ? editPriceModal?.product?.preferred_title : editPriceModal?.product?.title} ${editPriceModal?.product?.preferred_title !== editPriceModal?.product?.variants[0]?.title && editPriceModal?.product?.title !== editPriceModal?.product?.variants[0]?.title ? ` - ${editPriceModal?.product?.variants[0]?.title}` : ""} • ${displayCurrency(editPriceModal?.product?.variants[0]?.price)} ${t("per item")}`}
                    size="small"
                    onCancel={closeEditPriceModal}
                    onOk={handleUpdatePrice}
                    okText={t("Save changes")}>
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                type="number"
                                label={t("Price")}
                                prefix={facilityStore?.facility?.default_currency_symbol}
                                value={editPriceModal?.price}
                                onChange={e => updateEditPriceModal({ price: e.target.value })} />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>

            </Portal>

            <ExchangeItemsModal
                open={exchangeItemsModal?.isOpen}
                onCancel={closeExchangeItemsModal}
                onOk={items => addReplacementItems(items)}
                itemReplacementTitle={exchangeItemsModal?.itemReplacementTitle}
                presetItems={exchangeItemsModal?.presetItems}
            />

            <ExchangeCheckoutSheet
                open={checkoutOpen}
                summary={summary}
                order={order}
                submitting={submitting}
                onCancel={() => setCheckoutOpen(false)}
                onConfirmRefund={refundTransactions => void handleConfirmRefund(refundTransactions)}
                onConfirmCollect={paymentTransaction => void handleConfirmCollect(paymentTransaction)}
            />

            <StripeTerminalSheet />
            <StripeTerminalRefundSheet />

            <Portal isMounted={cancelModal?.isOpen}>
                <Popup
                    open={cancelModal?.isOpen}
                    title={t("Discard this exchange?")}
                    description={t("Your exchange is incomplete. Leaving now will discard all changes made during this exchange.")}
                    type="warning"
                    cancelText={t("Discard")}
                    okText={t("Continue exchange")}
                    onCancel={() => history?.push(`/admin/order/${orderId}`)}
                    onOk={closeCloseModal}
                    closable={false}
                    backDropCancel={false}
                />
            </Portal>
        </Page>
    );
}

function buildExchangeParams(groups?: Array<IExchangeItemGroup>): IExchangeGroup[] {
    if (!groups?.length) {
        return [];
    }

    const exchanges: IExchangeGroup[] = [];

    groups.forEach(group => {
        if (!group?.new_items?.length || !group?.previous_items?.length) {
            return;
        }

        const returnQty = new Map<number, number>();
        group.previous_items.forEach(item => {
            if (!item?.id) {
                return;
            }
            returnQty.set(item.id, (returnQty.get(item.id) ?? 0) + 1);
        });

        const return_items = Array.from(returnQty.entries()).map(([line_item_id, quantity]) => ({
            line_item_id,
            quantity,
        }));

        const exchange_items = group.new_items
            .map(item => ({
                variant_id: Number(item.variants?.[0]?.id),
                quantity: Number(item.variants?.[0]?.quantity ?? 1),
                price: Number(item.variants?.[0]?.price ?? 0),
            }))
            .filter(item => item.variant_id > 0 && item.quantity > 0);

        if (return_items.length > 0 && exchange_items.length > 0) {
            exchanges.push({ return_items, exchange_items });
        }
    });

    return exchanges;
}